import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Download, FileText, Upload, FileCode } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { ExportFormatModal } from './ExportFormatModal';
import { useDiagramExport } from '../../hooks/export/useDiagramExport';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { ReportExportModal } from './ReportExportModal';
import { useReportExport } from '../../hooks/export/useReportExport';
import type { ExportReportType } from '../../domains/export/models/ExportRequest';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { useDiagramStore } from '../../stores/diagramStore';
import { useDiagramInfo } from '../../hooks/useDiagramInfo';
import type { RdmDocument, RdmDocumentType } from '../../domains/rdm';
import { replaceSceneInWorkspace } from '../../lib/diagram/replaceSceneInYDoc';
import { validateFinalShapeCountOrPromptUpgrade } from '../../utils/shapeLimitUtils';
import { exportToBpmnXml } from '../../domains/process/export/bpmnXmlExporter';
import { ProcessTracking } from '../../domains/process/services/ProcessTracking';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { toast } from 'sonner';

export const OPEN_EXPORT_EVENT = 'export:open';

type RdmEditorModule = typeof import('../../domains/rdm/editor');

let rdmEditorModulePromise: Promise<RdmEditorModule> | null = null;

function loadRdmEditorModule(): Promise<RdmEditorModule> {
  if (!rdmEditorModulePromise) {
    rdmEditorModulePromise = import('../../domains/rdm/editor').catch((error) => {
      rdmEditorModulePromise = null;
      throw error;
    });
  }
  return rdmEditorModulePromise;
}

const LazyRdmExportModal = lazy(async () => {
  const module = await loadRdmEditorModule();
  return { default: module.RdmExportModal };
});

const LazyRdmImportModal = lazy(async () => {
  const module = await loadRdmEditorModule();
  return { default: module.RdmImportModal };
});

interface ExportControlsProps {
  diagramId?: string;
  selectedShapeIds?: string[];
}

/**
 * Lightweight presentation component that wires the export button to the
 * domain hook and surfaces the modal when needed.
 */
export const ExportControls: React.FC<ExportControlsProps> = ({ diagramId, selectedShapeIds }) => {
  const { t } = useTranslation('diagram');
  const { t: tc } = useTranslation('common');
  const { isRdmImportExportEnabled, loading: featureFlagsLoading } = useFeatureFlags();
  const showRdmImportExport = !featureFlagsLoading && isRdmImportExportEnabled();
  const { doc, downloadBackup, diagramType } = useDiagramDocument();
  const { data: diagramInfo } = useDiagramInfo(diagramId);
  const loadScene = useDiagramStore((state) => state.loadScene);
  const [isRdmImportOpen, setIsRdmImportOpen] = useState(false);
  const [isRdmExportOpen, setIsRdmExportOpen] = useState(false);
  const [isRdmModuleLoading, setIsRdmModuleLoading] = useState(false);
  const [rdmText, setRdmText] = useState('');
  const [rdmDocument, setRdmDocument] = useState<RdmDocument | null>(null);

  const {
    isOpen,
    open,
    onModalOpenChange,
    isSubmitting,
    isSaving,
    isDownloading,
    error,
    job,
    handleConfirm,
    handleCancel,
  } = useDiagramExport({ diagramId, selectedShapeIds });

  const {
    isOpen: isReportModalOpen,
    open: openReportModal,
    onModalOpenChange: onReportModalOpenChange,
    isSubmitting: isReportSubmitting,
    isSaving: isReportSaving,
    isDownloading: isReportDownloading,
    job: reportJob,
    error: reportError,
    startReportExport,
    handleCancel: handleReportCancel,
  } = useReportExport({ diagramId });

  const hasSelection = Boolean(selectedShapeIds && selectedShapeIds.length > 0);
  const selectionLabel = hasSelection
    ? t('export.includesSelected', { count: selectedShapeIds!.length })
    : t('export.formatOptions');

  const handleReportStart = async (selection: {
    option: { id: ExportReportType; reportVariant?: string };
    format: 'docx' | 'pdf' | 'csv' | 'json';
    progressSince?: string;
  }) => {
    const { option, format, progressSince } = selection;
    await startReportExport({
      reportType: option.id,
      reportVariant: option.reportVariant,
      format,
      quality: 'normal',
      ...(progressSince ? { progressSince } : {}),
    });
  };

  const loadRdmForAction = useCallback(async (): Promise<RdmEditorModule | null> => {
    if (isRdmModuleLoading) return null;

    setIsRdmModuleLoading(true);
    try {
      return await loadRdmEditorModule();
    } catch (error) {
      const message = error instanceof Error ? error.message : tc('export.unknownError');
      toast.error(tc('export.generateRdmFailed', { message }));
      return null;
    } finally {
      setIsRdmModuleLoading(false);
    }
  }, [isRdmModuleLoading, tc]);

  const handleOpenRdmImport = useCallback(async () => {
    const module = await loadRdmForAction();
    if (!module) return;
    setIsRdmImportOpen(true);
  }, [loadRdmForAction]);

  const handleOpenRdmExport = useCallback(async () => {
    const module = await loadRdmForAction();
    if (!module) return;

    const { nodes, edges } = useDiagramStore.getState();

    const rdmType = (() => {
      switch (diagramType) {
        case 'flowchart':
          return 'process';
        case 'org':
          return 'orgchart';
        case 'genogram':
          return 'genogram';
        case 'generic':
          return 'generic';
        default:
          return undefined;
      }
    })() as RdmDocumentType | undefined;

    try {
      const nextRdmText = module.rdmService.fromRuntimeModel(nodes, edges, {
        ...(rdmType ? { type: rdmType } : {}),
        title: diagramInfo?.name || `diagram-${diagramId ?? 'local'}`,
      });
      const parsed = module.rdmService.parseAndValidate(nextRdmText);

      if (!parsed.success || !parsed.document) {
        toast.error(tc('export.generateRdmFailed', { message: parsed.errors.map((error) => error.message).join('; ') }));
        return;
      }

      setRdmText(nextRdmText);
      setRdmDocument(parsed.document);
      setIsRdmExportOpen(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : tc('export.unknownError');
      toast.error(tc('export.generateRdmFailed', { message }));
    }
  }, [diagramId, diagramInfo?.name, diagramType, loadRdmForAction, tc]);

  const handleBpmnExport = useCallback(() => {
    const { nodes, edges } = useDiagramStore.getState();
    const processName = diagramInfo?.name || 'Process';
    const xml = exportToBpmnXml(nodes, edges, { processName });
    const blob = new Blob([xml], { type: 'application/xml' });
    if (diagramId) {
      ProcessTracking.bpmnExported({
        diagramId,
        format: 'bpmn-xml',
        sizeBytes: blob.size,
      });
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${processName.replace(/\s+/g, '-').toLowerCase()}.bpmn`;
    a.click();
    URL.revokeObjectURL(url);
  }, [diagramInfo?.name, diagramId]);

  const handleRdmImport = useCallback(async (document: RdmDocument) => {
    const module = await loadRdmEditorModule();
    const { nodes, edges } = await module.rdmService.toLayoutedRuntimeModel(document);
    const store = useDiagramStore.getState();

    replaceSceneInWorkspace({
      doc,
      nodes,
      edges,
      loadScene,
      validateFinalScene: (scene) => validateFinalShapeCountOrPromptUpgrade(
        store,
        scene.nodes.length + scene.edges.length,
      ),
    });
  }, [doc, loadScene]);

  const exportOptionCount =
    1 +
    (showRdmImportExport ? 1 : 0) +
    (diagramType === 'flowchart' ? 1 : 0) +
    (diagramType === 'genogram' ? 1 : 0);
  const shouldShowDropdown = exportOptionCount > 1;

  // Listen for external events to open export modal (e.g., from GenogramAssistantPanel)
  useEffect(() => {
    const handleOpenExport = () => {
      if (diagramId) {
        open();
      }
    };
    window.addEventListener(OPEN_EXPORT_EVENT, handleOpenExport);
    return () => window.removeEventListener(OPEN_EXPORT_EVENT, handleOpenExport);
  }, [diagramId, open]);

  return (
    <>
      <div className="flex items-center gap-2">
        {showRdmImportExport && (
          <Button
            variant="outline"
            size="sm"
            disabled={!diagramId || isRdmModuleLoading}
            aria-busy={isRdmModuleLoading}
            data-testid="tid.editor.import"
            data-event-click="header.import.rdm.click"
            onClick={() => void handleOpenRdmImport()}
          >
            <Upload className="h-4 w-4" />
            {tc('export.import')}
          </Button>
        )}

        {shouldShowDropdown ? (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!diagramId}
                    data-testid="tid.editor.export"
                  >
                    <Download className="h-4 w-4" />
                    {t('export.export')}
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{tc('accessibility.exportDiagram')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                data-testid="tid.editor.export.diagram"
                data-event-click="header.export.click"
                onClick={() => open()}
                disabled={!diagramId}
                className="flex items-start gap-2"
              >
                <Download className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-foreground">
                    {hasSelection ? t('export.exportSelectedShapes', { count: selectedShapeIds!.length }) : t('export.exportDiagram')}
                  </span>
                  <span className="text-xs text-muted-foreground">{selectionLabel}</span>
                </span>
              </DropdownMenuItem>
              {showRdmImportExport && (
                <DropdownMenuItem
                  data-testid="tid.editor.export.rdm"
                  data-event-click="header.export.rdm.click"
                  onClick={() => void handleOpenRdmExport()}
                  disabled={!diagramId || isRdmModuleLoading}
                  aria-busy={isRdmModuleLoading}
                  className="flex items-start gap-2"
                >
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-foreground">{tc('export.exportRdmTitle')}</span>
                    <span className="text-xs text-muted-foreground">{tc('export.exportRdmSubtitle')}</span>
                  </span>
                </DropdownMenuItem>
              )}
              {diagramType === 'flowchart' && (
                <DropdownMenuItem
                  data-testid="tid.editor.export.bpmn"
                  data-event-click="header.export.bpmn.click"
                  onClick={() => handleBpmnExport()}
                  disabled={!diagramId}
                  className="flex items-start gap-2"
                >
                  <FileCode className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-foreground">{tc('export.exportBpmnTitle')}</span>
                    <span className="text-xs text-muted-foreground">{tc('export.exportBpmnSubtitle')}</span>
                  </span>
                </DropdownMenuItem>
              )}
              {diagramType === 'genogram' && (
                <DropdownMenuItem
                  data-testid="tid.editor.export.report"
                  data-event-click="header.export.report.click"
                  onClick={() => openReportModal()}
                  disabled={!diagramId}
                  className="flex items-start gap-2"
                >
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-foreground">{t('export.generateReport')}</span>
                    <span className="text-xs text-muted-foreground">{t('export.clinicalReport')}</span>
                  </span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={!diagramId}
                data-testid="tid.editor.export"
                data-event-click="header.export.click"
                onClick={() => open()}
              >
                <Download className="h-4 w-4" />
                {t('export.export')}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{tc('accessibility.exportDiagram')}</TooltipContent>
          </Tooltip>
        )}
      </div>

      <ExportFormatModal
        open={isOpen}
        onOpenChange={onModalOpenChange}
        onConfirm={handleConfirm}
        isSubmitting={isSubmitting}
        error={error?.message ?? null}
        job={job}
        onCancel={handleCancel}
        isSaving={isSaving}
        isDownloading={isDownloading}
        selectedShapeIds={selectedShapeIds}
        onDownloadBackup={downloadBackup}
      />
      <ReportExportModal
        open={isReportModalOpen}
        onOpenChange={onReportModalOpenChange}
        onStart={handleReportStart}
        isSubmitting={isReportSubmitting}
        job={reportJob}
        error={reportError ?? null}
        isSaving={isReportSaving}
        isDownloading={isReportDownloading}
        onCancel={handleReportCancel}
      />
      {isRdmExportOpen && (
        <Suspense fallback={<span role="status" className="sr-only">{tc('loading')}</span>}>
          <LazyRdmExportModal
            open={isRdmExportOpen}
            onOpenChange={setIsRdmExportOpen}
            rdmDocument={rdmDocument}
            rdmText={rdmText}
          />
        </Suspense>
      )}
      {isRdmImportOpen && (
        <Suspense fallback={<span role="status" className="sr-only">{tc('loading')}</span>}>
          <LazyRdmImportModal
            open={isRdmImportOpen}
            onOpenChange={setIsRdmImportOpen}
            onImport={handleRdmImport}
          />
        </Suspense>
      )}
    </>
  );
};
